import { format, parseISO } from 'date-fns';
import { ChangeEvent, ReactElement } from 'react';
import { renderToString } from 'react-dom/server';
import { z } from 'zod';
import { ApplicantType, Period } from '@brighte/validation';
import { AddressType, QuoteStatus } from '../graphql';
import { KnownErrors } from './constants';

export type FormatDollarOptions = {
  hideZeroCents: boolean;
  zeroValue: string;
};

const defaultOptions: FormatDollarOptions = { hideZeroCents: false, zeroValue: 'No' };

export function formatDollars(amountInDollars: number | string | undefined, options?: FormatDollarOptions) {
  const optionsToUse = { ...defaultOptions, ...options };
  const parsedAmount = typeof amountInDollars === 'string' ? parseFloat(amountInDollars) : amountInDollars;

  if (parsedAmount === undefined || parsedAmount === 0) {
    return optionsToUse.zeroValue;
  }

  const amountToFormat = optionsToUse.hideZeroCents ? Math.round(parsedAmount) : parsedAmount;
  const formattedAmount = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amountToFormat);

  return optionsToUse.hideZeroCents ? formattedAmount.replace('.00', '') : formattedAmount;
}

export function formatAddress(address: AddressType) {
  const { unitNumber, streetNumber, streetName, suburb, state, postcode } = address;
  return `${unitNumber ? unitNumber + '/' : ''}${streetNumber} ${streetName}, ${suburb} ${state} ${postcode}`;
}

export const formatDate = (date, dateFormat = 'dd/MM/yyyy') => (date ? format(parseISO(date), dateFormat) : '');

export const extractStringFromComponent = (component: ReactElement) => {
  const htmlString = renderToString(component);
  // Insert '; ' before each closing list-item tag so nested TextList sub-bullets
  // are delimited when the result is used as plain text (e.g. submission payload).
  const withListSeparators = htmlString.replace(/<\/li>/gi, '; </li>');
  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(withListSeparators, 'text/html');
  return (parsedDocument.body.textContent ?? '').replace(/;\s*$/, '').trim();
};

export const setCheckboxValue = (event: ChangeEvent<HTMLInputElement>, label: ReactElement | string, setValue) => {
  const isChecked = event.target.value;
  const name = event.target.name;
  if (!isChecked) {
    setValue(name, '');
  } else {
    if (typeof label === 'string') {
      setValue(name, label);
    } else {
      const value = extractStringFromComponent(label) || '';
      setValue(name, value);
    }
  }
};

type ErrorWithMessage = {
  message: string;
};

const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof (error as Record<string, unknown>).message === 'string';

const toErrorWithMessage = (maybeError: unknown): ErrorWithMessage => {
  if (isErrorWithMessage(maybeError)) {
    return maybeError;
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
};

export const getErrorMessage = (error: unknown) => toErrorWithMessage(error).message;
export const capitalise = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const getFormFields = <T extends z.ZodTypeAny>(schema: T, discriminator: string): string[] => {
  // get zod object keys recursively
  // make sure schema is not null or undefined
  if (schema === null || schema === undefined) return [];
  // check if schema is nullable or optional
  if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional)
    return getFormFields(schema.unwrap(), discriminator);
  // check if schema is an array
  if (schema instanceof z.ZodArray) return getFormFields(schema.element, discriminator);
  // check if schema is an object
  if (schema instanceof z.ZodObject) {
    // get key/value pairs from schema
    const entries = Object.entries(schema.shape);
    // loop through key/value pairs
    return entries.flatMap(([key, value]) => {
      // get nested keys
      const nested =
        value instanceof z.ZodType ? getFormFields(value, discriminator).map(subKey => `${key}.${subKey}`) : [];
      // return nested keys
      return nested.length ? nested : key;
    });
  }
  // check if schema is an effects
  if (schema instanceof z.ZodEffects) {
    return getFormFields(schema._def.schema, discriminator);
  }

  if (schema instanceof z.ZodDiscriminatedUnion) {
    const entries = schema._def.optionsMap.entries();

    for (const [id, fields] of entries) {
      if (id === discriminator) {
        return getFormFields(fields, discriminator);
      }
    }

    // If no option matched the discriminator (e.g. nested union keyed on a different field),
    // collect the union of all fields from every option so the caller can read whichever are present.
    const allFields = new Set<string>();
    for (const [, fields] of schema._def.optionsMap.entries()) {
      getFormFields(fields, discriminator).forEach(field => allFields.add(field));
    }
    return [...allFields];
  }
  // check if schema is an intersection
  if (schema instanceof z.ZodIntersection) {
    const leftIntersection = schema._def.left;
    const rightIntersection = schema._def.right;
    const formFields: string[] = [];

    [leftIntersection, rightIntersection].forEach(intersection => {
      if (intersection instanceof z.ZodObject) {
        formFields.push(...getFormFields(intersection, discriminator));
      }
    });

    [leftIntersection, rightIntersection].forEach(intersection => {
      if (intersection instanceof z.ZodDiscriminatedUnion) {
        const entries = intersection._def.optionsMap.entries();

        if (!discriminator) throw new Error('Discriminator is required to extract form fields');

        for (const [id, fields] of entries) {
          if (id === discriminator) {
            formFields.push(...getFormFields(fields, discriminator));
          }
        }
      }
    });

    return formFields;
  }
  return [];
};

export const isFieldExists = (names: string[], field: string) => names.some(name => name === field);

export function isJointApplicant() {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('joint') === 'true';
}

export function getTrackingId() {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('trackingId') ?? '';
}

export function getApplicantTypeAndTrackingIdFromUrl() {
  const trackingId = getTrackingId();
  const isJoint = isJointApplicant();

  return {
    trackingId,
    applicantType: isJoint ? ApplicantType.Joint : ApplicantType.Primary,
  };
}

export function deSlug(slug?: string) {
  if (!slug) {
    return '';
  }

  const words = slug.split('-');
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function pascalToCamelCase(value: string) {
  if (value) {
    return value.charAt(0).toLowerCase() + value.slice(1);
  }
  return value;
}

export function toSnakeCase(value: string) {
  return value.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

// Structural subset of a Datadog RUM event — only the fields beforeSend reads.
type DDRumEventLike = {
  type?: string;
  source?: string;
  date?: number;
  error?: { message?: string };
  action?: { type?: string; target?: unknown; [key: string]: unknown };
  context?: { message?: unknown; name?: unknown };
};

/**
 * Auth/session errors are expected user conditions (expired or invalid refresh
 * token), not application defects. A single session expiry surfaces the same
 * message through several layers, so we drop them from the RUM stream entirely —
 * keeping them out of error-count monitors and off the events timeline.
 */
const EXPECTED_AUTH_ERROR_MESSAGES = ['Error fetching access token', 'Error with refresh token present', 'TOKEN_ERROR'];

/**
 * Re-renders and child components report the same logical error many times within
 * a few milliseconds. Collapse identical events (same type + message/name) seen
 * inside this window down to a single RUM event.
 */
const DD_DEDUP_WINDOW_MS = 5_000;
// Module-scoped by design: this is a browser SPA, so the map lives per-tab and is
// never shared across user sessions. It is not safe to import into an SSR context.
const recentEventTimestamps = new Map<string, number>();

// Test-only: reset the dedup map so each test case starts from a clean window.
// Without this, module-scoped fingerprints leak between tests. Call in `beforeEach`.
export const _testOnlyClearDDRumDedup = (): void => {
  recentEventTimestamps.clear();
};

const isExpectedAuthError = (message?: unknown): boolean =>
  typeof message === 'string' && EXPECTED_AUTH_ERROR_MESSAGES.some(expected => message.includes(expected));

const getDDActionName = (event: DDRumEventLike): string => {
  const target = event.action?.target;
  if (target && typeof target === 'object' && 'name' in target && typeof target.name === 'string') {
    return target.name;
  }
  return '';
};

const getDDEventFingerprint = (event: DDRumEventLike): string => {
  const errorMessage = event.error?.message;
  if (errorMessage) return `error:${errorMessage}`;

  // Only dedupe custom (developer-emitted) actions — never Datadog's auto-tracked
  // clicks/scrolls, where two identical events are real, distinct interactions.
  if (event.type === 'action' && event.action?.type === 'custom') {
    const message = typeof event.context?.message === 'string' ? event.context.message : '';
    // Many custom actions carry a distinguishing `context.name` but no `message`
    // (e.g. StepNavigator's "Before calling save step"). Fold it into the
    // fingerprint so genuinely-different actions aren't collapsed within the window.
    const contextName = typeof event.context?.name === 'string' ? event.context.name : '';
    return `action:${getDDActionName(event)}:${message}:${contextName}`;
  }

  return '';
};

const pruneStaleFingerprints = (now: number): void => {
  for (const [fingerprint, timestamp] of recentEventTimestamps) {
    if (now - timestamp >= DD_DEDUP_WINDOW_MS) recentEventTimestamps.delete(fingerprint);
  }
};

export const isDDRumKnownError = (event: DDRumEventLike): boolean => {
  const errorMessage = event.error?.message;

  if (errorMessage) {
    const isKnown = Object.values(KnownErrors).some(knownError => errorMessage.includes(knownError));
    if (isKnown) return false;
    if (isExpectedAuthError(errorMessage)) return false;
  }

  // Drop the auth-error "echo" custom actions (e.g. addAction('error', { message }))
  // so one session expiry doesn't spray the timeline.
  if (event.type === 'action' && isExpectedAuthError(event.context?.message)) return false;

  // Suppress duplicate error/action events fired within the dedup window.
  const fingerprint = getDDEventFingerprint(event);
  if (fingerprint) {
    const now = event.date ?? Date.now();
    // Prune on every call — including suppressed duplicates — so a burst of dropped
    // events still bounds the map to the last few seconds, not just new writes.
    pruneStaleFingerprints(now);
    const lastSeen = recentEventTimestamps.get(fingerprint);
    if (lastSeen !== undefined && now - lastSeen < DD_DEDUP_WINDOW_MS) return false;
    recentEventTimestamps.set(fingerprint, now);
  }

  return true;
};

export const maskString = (value: string, revealedLength = 4, mask = '*') =>
  `${value}`.slice(value.length <= revealedLength ? value.length : -revealedLength).padStart(`${value}`.length, mask);

export const maskEmail = (email: string) => {
  const name = email.split('@')[0];
  const domain = email.split('@')[1];
  const maskedName = maskString(name);
  return `${maskedName}${domain ? `@${domain}` : ''}`;
};

export const isFloat = (value: number): boolean => {
  return Number(value) === value && !Number.isInteger(value);
};

export const getAdminUserId = () => {
  const COOKIE_ADMIN_NAME = 'brighte_admin';

  const adminUser = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${COOKIE_ADMIN_NAME}=`))
    ?.split('=')[1];

  return adminUser;
};

export const convertAmountToMonthlyPeriod = (amount: number, period: Period): number => {
  switch (period) {
    case Period.Weekly:
      return (amount * 52) / 12;
    case Period.Fortnightly:
      return (amount * 26) / 12;
    case Period.Annually:
      return amount / 12;
    default:
      return amount;
  }
};

export const accessibleQuote = (quoteStatus: QuoteStatus): boolean => {
  const accessibleStatuses = [QuoteStatus.SENT, QuoteStatus.STARTED];
  return accessibleStatuses.includes(quoteStatus);
};

export type ManualOccupationJobTitleMatchingType = {
  jobTitle: string;
  isFetched: boolean;
  isMatching: boolean;
  isMatched: boolean;
};
